import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CheckCircle, XCircle, Info, AlertTriangle, FileText, Calendar, DollarSign } from 'lucide-react';

interface EvidenceItem {
  type: 'match' | 'mismatch' | 'info' | 'warning';
  label: string;
  value: string;
  detail?: string;
}

interface EvidenceDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  evidence: EvidenceItem[];
  relatedData?: {
    label: string;
    items: { key: string; value: string }[];
  }[];
}

const evidenceIcons = {
  match: <CheckCircle className="h-4 w-4 text-success" />,
  mismatch: <XCircle className="h-4 w-4 text-destructive" />,
  info: <Info className="h-4 w-4 text-primary" />,
  warning: <AlertTriangle className="h-4 w-4 text-warning" />,
};

export function EvidenceDrawer({
  open,
  onOpenChange,
  title,
  description,
  evidence,
  relatedData,
}: EvidenceDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader className="mb-4">
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)]">
          <div className="space-y-6 pr-4">
            {/* Evidence Points */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Why this was flagged
              </h4>
              <div className="space-y-2">
                {evidence.map((item, idx) => (
                  <Card key={idx} className="p-3">
                    <div className="flex items-start gap-3">
                      {evidenceIcons[item.type]}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-foreground">{item.label}</span>
                          <Badge
                            variant={item.type === 'match' ? 'default' : item.type === 'mismatch' ? 'destructive' : 'secondary'}
                            className="text-xs shrink-0"
                          >
                            {item.value}
                          </Badge>
                        </div>
                        {item.detail && (
                          <p className="text-xs text-muted-foreground mt-1">{item.detail}</p>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Related Data */}
            {relatedData && relatedData.length > 0 && (
              <>
                {relatedData.map((section, sectionIdx) => (
                  <div key={sectionIdx}>
                    <h4 className="text-sm font-semibold text-foreground mb-3">{section.label}</h4>
                    <Card className="p-4">
                      <div className="space-y-2">
                        {section.items.map((item, itemIdx) => (
                          <div key={itemIdx} className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{item.key}</span>
                            <span className="font-medium text-foreground">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>
                ))}
              </>
            )}

            {/* Sentinel Explanation */}
            <div className="bg-surface rounded-lg p-4 border border-border">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Info className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">Sentinel Analysis</h4>
                  <p className="text-xs text-muted-foreground">
                    This item was flagged based on pattern matching against your historical data
                    and configured thresholds. Review the evidence points above to make an informed decision.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
